import {
  Stack,
  Radio,
  Inline,
  P,
  Spinner,
  Checkbox,
  Dropdown,
  H5,
  Message,
  Grid
} from "@adfenix/alexandria";
import { ProgramService } from "@adfenix/types";
import { useFormContext } from "react-hook-form";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDisabledFields } from "./useDisabledFields";
import { useMetaTemplateContext } from "./campaign/util/CampaignTemplateContext";
import { useOfficeAndHq } from "hooks/useOfficeAndHq";
import { useTranslation } from "react-i18next";
import { formMaxWidth } from "./bundle";
import { QuedroIntegrationServiceApi } from "api/services";

type CustomTargetingMethod = "CustomAudienceSphere" | "CustomAudienceFocusArea";

const {
  limitedHqConfiguration: {
    authV1LimitedHqConfigurationDetail: getLimitedHqConfiguration
  }
} = QuedroIntegrationServiceApi;

/**
 * Configurations for targeting.
 *
 * The "Dynamic retargeting with geo weighting" is our default targeting method.
 * In addition to this a program can also be configured to use a custom data source.
 * You should be able to use one or the other or both.
 *
 * Currently missing:
 *  No property in the back-end for the dynamic geo targeting.
 *  No property in the back-end for the custom data source.
 */
export const Targeting = () => {
  const { t } = useTranslation();

  const { hq } = useOfficeAndHq();
  const disabledFields = useDisabledFields();

  const {
    error,
    isInitialLoading: isLoading,
    data
  } = useQuery(
    ["quedroHqSettings", hq?.id ?? ""],
    () => getLimitedHqConfiguration(hq!.id),
    { enabled: Boolean(hq?.id), retry: false }
  );

  const { watch, setValue } = useFormContext<ProgramService.ProgramModel>();

  const { campaignKey } = useMetaTemplateContext();

  const targetingSettings = watch(`${campaignKey}.targetingSettings`, []) ?? [];

  const [customTargetingMethod, setCustomTargetingMethod] = useState<
    CustomTargetingMethod | undefined
  >(
    targetingSettings.find(x => x.adTargetingType !== "LocationArea")
      ?.adTargetingType as CustomTargetingMethod
  );

  const [standardTargetingMethod, setStandardTargetingMethod] = useState(
    targetingSettings.some(x => x.adTargetingType === "LocationArea")
  );

  const [firstPartyDataEnabled, setFirstPartyDataEnabled] = useState(
    targetingSettings.some(
      x =>
        x.adTargetingType === "CustomAudienceSphere" ||
        x.adTargetingType === "CustomAudienceFocusArea"
    )
  );

  const [customTargetingSource, setCustomTargetingSource] = useState<
    "Office" | "Agent"
  >(
    (targetingSettings.find(x => x.adTargetingType !== "LocationArea")
      ?.customTargetingSource as "Office" | "Agent") ?? "Agent"
  );

  useEffect(() => {
    setValue(
      `${campaignKey}.targetingSettings`,
      toTargetSettingsCollection(
        customTargetingMethod,
        standardTargetingMethod,
        customTargetingSource,
        firstPartyDataEnabled
      ),
      { shouldDirty: true }
    );
  }, [
    campaignKey,
    customTargetingMethod,
    customTargetingSource,
    firstPartyDataEnabled,
    setValue,
    standardTargetingMethod
  ]);

  if (isLoading) {
    return <Spinner size={50} />;
  }

  // Most customers will get a 404 when requesting the quedro integration service hq configurations
  // In that case the integration should be set up through the hq-settings page.
  if (error) {
    return <P> {t("programBuilder.program.details.targeting.error")}</P>;
  }

  return (
    <Stack spacing="l" style={{ maxWidth: formMaxWidth }}>
      <H5>{t("programBuilder.program.details.targeting.header")}</H5>
      <Grid columns={["1fr"]} gap="l">
        <Checkbox
          name="Standard targeting"
          label={t("programBuilder.program.details.targeting.standart.label")}
          checked={standardTargetingMethod}
          readOnly={disabledFields(
            "bundleGroups.bundles.campaignTemplates.targetingSettings"
          )}
          onChange={e => {
            setStandardTargetingMethod(e.target.checked);
          }}
        />

        <Checkbox
          name="Custom data source"
          label={t(
            "programBuilder.program.details.targeting.firstPartyData.label"
          )}
          checked={firstPartyDataEnabled}
          readOnly={disabledFields(
            "bundleGroups.bundles.campaignTemplates.targetingSettings"
          )}
          onChange={e => {
            setFirstPartyDataEnabled(e.target.checked);
          }}
        />
      </Grid>

      <Grid columns={["1fr", "1fr 1fr 1fr"]} gap="l">
        <Dropdown
          disabled={
            disabledFields(
              "bundleGroups.bundles.campaignTemplates.targetingSettings"
            ) || !firstPartyDataEnabled
          }
          label={t(
            "programBuilder.program.details.targeting.targetingSource.label"
          )}
          items={["Office", "Agent"]}
          name="targetingSource"
          value={customTargetingSource}
          onChange={value => setCustomTargetingSource(value)}
        />

        <Stack spacing="s">
          <P>
            {t("programBuilder.program.details.targeting.methodology.header")}
          </P>
          <Inline>
            <Radio
              onChange={() => {
                setCustomTargetingMethod("CustomAudienceSphere");
              }}
              checked={customTargetingMethod === "CustomAudienceSphere"}
              label={t("programBuilder.program.details.targeting.sphere.label")}
              value="Sphere"
              name="targetingMethod"
              id={`${campaignKey}-sphere`}
              disabled={
                disabledFields(
                  "bundleGroups.bundles.campaignTemplates.targetingSettings"
                ) ||
                !firstPartyDataEnabled ||
                !data?.allowSphereContacts
              }
            />

            <Radio
              onChange={() => {
                setCustomTargetingMethod("CustomAudienceFocusArea");
              }}
              checked={customTargetingMethod === "CustomAudienceFocusArea"}
              label={t(
                "programBuilder.program.details.targeting.focusArea.label"
              )}
              value="FocusArea"
              name="targetingMethod"
              id={`${campaignKey}-focus-area`}
              disabled={
                disabledFields(
                  "bundleGroups.bundles.campaignTemplates.targetingSettings"
                ) || !firstPartyDataEnabled
              }
            />
          </Inline>
        </Stack>
        {!data?.allowSphereContacts && (
          <Message type="warning">
            {t("programBuilder.program.details.targeting.sphere.error")}
          </Message>
        )}
      </Grid>
    </Stack>
  );
};

const toTargetSettingsCollection = (
  customTargetingMethod: CustomTargetingMethod | undefined,
  standardTargetingMethod: boolean,
  customTargetingSource: ProgramService.SubjectType,
  firstPartyDataEnabled: boolean
) => {
  const newSettings: ProgramService.TargetingSettingModel[] = [];

  if (standardTargetingMethod) {
    newSettings.push({
      adTargetingType: "LocationArea",
      customTargetingSource
    });
  }

  if (firstPartyDataEnabled && customTargetingMethod) {
    newSettings.push({
      adTargetingType: customTargetingMethod,
      customTargetingSource
    });
  }

  return newSettings;
};
